import { useToast } from "@/hooks/use-toast";
import { ExpenseFormValues } from "@/schemas/expenseFormSchema";
import { useExpenses } from './useExpenses';
import { Expense, ExpenseCategory } from '@/types/expense';
import { useState, useEffect } from 'react';
import { toast } from "sonner";
import { useNavigate } from 'react-router-dom';

export const useTripManagement = () => {
  const navigate = useNavigate();
  const { expenses, addExpense, deleteExpense, clearExpenses } = useExpenses();
  const [currentTripName, setCurrentTripName] = useState<string>(() => {
    return localStorage.getItem('currentTripName') || "";
  });
  const [tripHistory, setTripHistory] = useState<any[]>(() => {
    const savedHistory = localStorage.getItem('tripHistory');
    return savedHistory ? JSON.parse(savedHistory) : [];
  });

  // Sync with localStorage whenever state changes
  useEffect(() => {
    if (currentTripName) {
      localStorage.setItem('currentTripName', currentTripName);
      // Save current trip data
      const currentTripData = {
        name: currentTripName,
        expenses,
        startDate: expenses[0]?.date || new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem('currentTripData', JSON.stringify(currentTripData));
    } else {
      localStorage.removeItem('currentTripData');
    }
    localStorage.setItem('tripHistory', JSON.stringify(tripHistory));
  }, [currentTripName, expenses, tripHistory]);

  // Load saved trip data on mount
  useEffect(() => {
    const savedTripData = localStorage.getItem('currentTripData');
    if (savedTripData) {
      const parsedData = JSON.parse(savedTripData);
      setCurrentTripName(parsedData.name);
      // Restore expenses if they're not already loaded
      if (expenses.length === 0 && parsedData.expenses.length > 0) {
        parsedData.expenses.forEach((expense: Expense) => {
          addExpense(expense);
        });
      }
    }
  }, []);

  const handleAddExpense = (values: ExpenseFormValues) => {
    const newExpense = {
      description: values.description,
      amount: Number(values.amount),
      category: values.category as ExpenseCategory,
      date: values.date.toISOString(),
      paidBy: values.paidBy,
      participants: values.participants || []
    };
    
    const expense = addExpense(newExpense);
    
    toast("Expense Added", {
      description: `${values.description} (₹${values.amount}) has been added.`
    });
    
    return expense;
  };

  const handleDeleteExpense = (id: string) => {
    deleteExpense(id);
    toast("Expense Deleted", {
      description: "The expense has been removed from your trip."
    });
  };

  const calculateTripStats = (expenses: Expense[]) => {
    const totalParticipants = new Set(expenses.flatMap(e => e.participants || [])).size;
    const categories = expenses.reduce((acc, exp) => {
      acc[exp.category] = (acc[exp.category] || 0) + Number(exp.amount);
      return acc;
    }, {} as Record<string, number>);
    
    const highestSpender = expenses.reduce((acc, exp) => {
      acc[exp.paidBy] = (acc[exp.paidBy] || 0) + Number(exp.amount);
      return acc;
    }, {} as Record<string, number>);

    return {
      totalParticipants,
      categoryBreakdown: categories,
      topSpender: Object.entries(highestSpender).sort((a, b) => b[1] - a[1])[0]
    };
  };

  const handleStartNewTrip = (tripName: string) => {
    setCurrentTripName(tripName);
    clearExpenses();
    toast.success("New Trip Started", {
      description: `Your new trip "${tripName}" has been started.`
    });
  };

  const handleEndTrip = () => {
    if (currentTripName && expenses.length > 0) {
      const stats = calculateTripStats(expenses);
      const completedTrip = {
        name: currentTripName,
        expenses: [...expenses],
        startDate: expenses[0]?.date || new Date().toISOString(),
        endDate: new Date().toISOString(),
        totalAmount: expenses.reduce((sum, exp) => sum + Number(exp.amount), 0),
        stats: {
          totalParticipants: stats.totalParticipants,
          categoryBreakdown: stats.categoryBreakdown,
          topSpender: stats.topSpender
        }
      };
      
      setTripHistory(prev => [...prev, completedTrip]);
      clearExpenses();
      setCurrentTripName("");
      localStorage.removeItem('currentTripData');
      
      toast.success("Trip Ended", {
        description: `Your trip "${currentTripName}" has been completed and moved to history.`
      });

      window.dispatchEvent(new Event('storage'));
      navigate('/?tab=history');
    } else {
      toast.error("Cannot End Trip", {
        description: "Please add at least one expense before ending the trip."
      });
    }
  };

  return {
    currentTripName,
    tripHistory,
    expenses,
    handleAddExpense,
    handleDeleteExpense,
    handleStartNewTrip,
    handleEndTrip
  };
};
